import { useState, useEffect, useCallback } from 'react';
import { SlideImage } from '../types';
import { apiService } from '../services/api';

export const useSlideImages = () => {
  const [slideImages, setSlideImages] = useState<SlideImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSlideImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getSlideImages();
      if (response.success) {
        setSlideImages(response.data);
      } else {
        setError(response.message || 'Failed to fetch slide images');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  const createSlideImage = useCallback(async (data: Omit<SlideImage, 'id' | 'createdAt' | 'updatedAt'>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.createSlideImage(data);
      if (response.success) {
        setSlideImages(prev => [...prev, response.data]);
        return response.data;
      } else {
        setError(response.message || 'Failed to create slide image');
        throw new Error(response.message || 'Failed to create slide image');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSlideImage = useCallback(async (id: string, data: Partial<SlideImage>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.updateSlideImage(id, data);
      if (response.success) {
        setSlideImages(prev => 
          prev.map(item => item.id === id ? response.data : item)
        );
        return response.data;
      } else {
        setError(response.message || 'Failed to update slide image');
        throw new Error(response.message || 'Failed to update slide image');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSlideImage = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.deleteSlideImage(id);
      if (response.success) {
        setSlideImages(prev => prev.filter(item => item.id !== id));
      } else {
        setError(response.message || 'Failed to delete slide image');
        throw new Error(response.message || 'Failed to delete slide image');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlideImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    slideImages,
    loading,
    error,
    refetch: fetchSlideImages,
    createSlideImage,
    updateSlideImage,
    deleteSlideImage,
  };
};